import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { getStateBySlug, slugify } from "@/data/states";
import { getAreasForCity } from "@/data/areas";
import { GeoSections } from "@/components/GeoSections";
import { buildLocationContent, breadcrumbSchema, geoCoords } from "@/data/geo-content";
import { ogImageMeta } from "@/lib/og-image";

export const Route = createFileRoute("/states/$state/$town/$area")({
  loader: ({ params }) => {
    const s = getStateBySlug(params.state);
    if (!s) throw notFound();
    const city = s.cities.find((c) => slugify(c) === params.town);
    if (!city) throw notFound();
    const areas = getAreasForCity(s.slug, params.town, city);
    const area = areas.find((a) => slugify(a) === params.area);
    if (!area) throw notFound();
    return { state: s, city, area, areas };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { state, city, area } = loaderData;
    const content = buildLocationContent({
      locationName: area,
      cityName: city,
      stateName: state.name,
      stateCode: state.code,
      scope: "area",
    });
    const title = `Free Vending in ${area}, ${city} ${state.code} | $0 Install`;
    const desc = `FREE vending machines for businesses in ${area}, ${city}, ${state.name}. Zero-cost placement, install, restocking & maintenance from local ${state.code} operators. We try to respond in 4–6 hrs.`;
    const url = `https://vendingmachinefree.com/states/${state.slug}/${slugify(city)}/${slugify(area)}`;
    const coords = geoCoords(state.code, `${area}-${city}`);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...ogImageMeta(),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: `Vending Machine Free.com — ${area}, ${city}, ${state.code}`,
              areaServed: { "@type": "Place", name: `${area}, ${city}, ${state.name}` },
              description: desc,
              url,
              geo: { "@type": "GeoCoordinates", latitude: coords.lat, longitude: coords.lng },
            },
            {
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: "Free Vending Machine Placement",
              areaServed: { "@type": "Place", name: `${area}, ${city}, ${state.name}` },
              provider: { "@type": "Organization", name: "Vending Machine Free.com", url: "https://vendingmachinefree.com" },
            },
            breadcrumbSchema([
              { name: "Home", url: "https://vendingmachinefree.com/" },
              { name: "USA Coverage", url: "https://vendingmachinefree.com/coverage" },
              { name: state.name, url: `https://vendingmachinefree.com/states/${state.slug}` },
              { name: city, url: `https://vendingmachinefree.com/states/${state.slug}/${slugify(city)}` },
              { name: area, url },
            ]),
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: content.faqs.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
          ]),
        },
      ],
    };
  },
  component: AreaPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <Link to="/" className="text-safety">
        Go Home
      </Link>
    </div>
  ),
  errorComponent: () => <div className="p-10">Error.</div>,
});

function AreaPage() {
  const { state, city, area, areas } = Route.useLoaderData();
  const content = buildLocationContent({
    locationName: area,
    cityName: city,
    stateName: state.name,
    stateCode: state.code,
    scope: "area",
  });
  const nearby = (areas as string[]).filter((a: string) => a !== area).slice(0, 6);
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link
            to="/"
            hash="contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x">
          <nav className="text-xs text-white/60 mb-4">
            <Link to="/" className="hover:text-safety">
              Home
            </Link>{" "}
            /{" "}
            <Link to="/coverage" className="hover:text-safety">
              USA Map
            </Link>{" "}
            /{" "}
            <Link to="/states/$state" params={{ state: state.slug }} className="hover:text-safety">
              {state.name}
            </Link>{" "}
            /{" "}
            <Link
              to="/states/$state/$town"
              params={{ state: state.slug, town: slugify(city) }}
              className="hover:text-safety"
            >
              {city}
            </Link>{" "}
            / <span className="text-white">{area}</span>
          </nav>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <MapPin className="h-4 w-4" /> {area} · {city}, {state.code}
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Vending Machines in {area}, {city}
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl text-lg">
            Local placement, install, restocking, and maintenance for {area} businesses — backed by
            our {city}, {state.name} route team at zero cost. We try to respond in less than 4hrs –
            6hrs.
          </p>
          <a
            href="#apply"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            {content.cta} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <GeoSections content={content} locationName={area} stateName={state.name} />

      {nearby.length > 0 && (
        <section className="py-14 bg-background">
          <div className="container-x">
            <h2 className="font-display text-3xl uppercase">
              Nearby Suburbs & Industrial Areas in {city}
            </h2>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {nearby.map((a: string) => (
                <Link
                  key={a}
                  to="/states/$state/$town/$area"
                  params={{ state: state.slug, town: slugify(city), area: slugify(a) }}
                  className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-safety hover:shadow-md transition"
                >
                  <MapPin className="h-5 w-5 text-safety" />
                  <span className="font-semibold text-sm">{a}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-10">
        <div className="container-x text-center flex flex-wrap justify-center gap-5 text-sm">
          <Link
            to="/states/$state/$town"
            params={{ state: state.slug, town: slugify(city) }}
            className="text-steel hover:text-safety font-semibold"
          >
            ← Back to {city}
          </Link>
          <Link
            to="/states/$state"
            params={{ state: state.slug }}
            className="text-steel hover:text-safety font-semibold"
          >
            {state.name}
          </Link>
          <Link to="/coverage" className="text-steel hover:text-safety font-semibold">
            USA Map
          </Link>
          <Link to="/" className="text-steel hover:text-safety font-semibold">
            Home
          </Link>
          <Link to="/" hash="contact" className="text-steel hover:text-safety font-semibold">
            Apply Now
          </Link>
        </div>
      </section>
    </div>
  );
}
